/*
 * JC_SN_CART_CONTENT_01. Intelligems owns assignment and persistence.
 * Enrollment is requested ONLY by CartDrawer.open(), never by page load/ATC.
 * One live drawer; the other arm lives in an inert template.
 * Setup and the pre-launch checklist: docs/cart-drawer-experiment.md.
 */
;(function () {
  if (window.SnuggliCartExperiment) return

  let arm = 'control'
  let opening = null
  let settled = false
  let pricingReady = false

  // Never submit a drawer whose displayed arm has not been repriced by Shopify.
  document.addEventListener('submit', (event) => {
    if (!pricingReady && event.target?.id === 'CartDrawer-Form') event.preventDefault()
  }, true)

  function config() {
    try {
      return JSON.parse(document.querySelector('[data-cart-experiment-config]')?.textContent || '{}')
    } catch {
      return {}
    }
  }

  function parseSection(html, selectedArm = arm) {
    const parsed = new DOMParser().parseFromString(html, 'text/html')
    if (selectedArm === 'variant') {
      const variant = parsed.querySelector('template[data-cart-variant]')?.content.querySelector('cart-drawer')
      const control = parsed.querySelector('cart-drawer')
      if (variant && control) control.replaceWith(variant)
    }
    return parsed
  }

  function ready(timeout = 1200) {
    if (window.igData?.user) return Promise.resolve(window.igData.user)
    return new Promise((resolve) => {
      const finish = () => {
        clearTimeout(timer)
        window.removeEventListener('ig:ready', finish)
        resolve(window.igData?.user || null)
      }
      const timer = setTimeout(finish, timeout)
      window.addEventListener('ig:ready', finish, { once: true })
    })
  }

  async function sectionHTML(drawer) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 2500)
    try {
      const url = new URL(window.routes?.cart_url || '/cart', window.location.origin)
      url.searchParams.set('section_id', drawer.dataset.sectionId)
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } })
      if (!response.ok) throw new Error('Cart section unavailable')
      const html = await response.text()
      const parsed = parseSection(html, 'control')
      if (
        !parsed.querySelector('cart-drawer') ||
        !parsed.querySelector('template[data-cart-variant]')?.content.querySelector('cart-drawer')
      ) {
        throw new Error('Cart section is incomplete')
      }
      return html
    } finally {
      clearTimeout(timer)
    }
  }

  function mount(drawer, html, selectedArm) {
    if (!drawer.isConnected || drawer.openCancelled) return
    const source = parseSection(html, selectedArm).querySelector('cart-drawer')
    if (!source || source.dataset.cartArm !== selectedArm) throw new Error('Cart arm unavailable')
    drawer.className = source.className
    drawer.dataset.cartArm = selectedArm
    drawer.innerHTML = source.innerHTML
    drawer.toggleAttribute('data-editor-preview', source.hasAttribute('data-editor-preview'))
    arm = selectedArm
  }

  function pricingError(drawer) {
    pricingReady = false
    settled = false
    opening = null
    drawer.querySelectorAll('[name="checkout"]').forEach((button) => { button.disabled = true })
    const error = drawer.querySelector('#CartDrawer-CartErrors')
    if (error) error.textContent = 'Could not update cart prices. Please reopen the cart to retry.'
  }

  async function pricingHTML(drawer, selectedArm) {
    const settings = config()
    const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: { _jc_cart_arm: selectedArm, _jc_cart_experiment: settings.experimentId || '' },
        sections: [drawer.dataset.sectionId, 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    })
    if (!response.ok) throw new Error('Cart pricing update failed')
    const data = await response.json()
    if (data.status || data.attributes?._jc_cart_arm !== selectedArm ||
        (data.attributes?._jc_cart_experiment || '') !== (settings.experimentId || '')) throw new Error('Cart pricing arm was not saved')
    const html = data.sections?.[drawer.dataset.sectionId]
    if (typeof html !== 'string' || !parseSection(html, selectedArm).querySelector('cart-drawer')) throw new Error('Repriced cart is unavailable')
    const bubbleHTML = data.sections?.['cart-icon-bubble']
    const bubble = document.getElementById('cart-icon-bubble')
    if (bubble && bubbleHTML) {
      const source = new DOMParser().parseFromString(bubbleHTML, 'text/html').querySelector('.shopify-section')
      if (source) bubble.innerHTML = source.innerHTML
    }
    return html
  }

  async function activate(drawer) {
    const settings = config()
    const preview = settings.allowPreview
      ? new URLSearchParams(window.location.search).get('jc_cart') || (settings.editor ? 'variant' : '')
      : ''

    if (preview === 'control' || preview === 'variant') {
      return preview
    }
    if (
      !settings.enabled ||
      !settings.experimentId ||
      !settings.controlId ||
      !settings.variantId ||
      settings.controlId === settings.variantId
    )
      return

    const user = await ready()
    if (!drawer.isConnected || drawer.openCancelled) return
    if (!user?.getTestGroup || !user?.getTestGroups || !user?.assignTestGroup) return

    // Validate both arms and usable markup BEFORE enrolling anyone. A failed
    // section fetch opens Control without requesting a new assignment.
    const groups = user.getTestGroups(settings.experimentId)
    if (
      !Array.isArray(groups) ||
      groups.length !== 2 ||
      !groups.some((group) => group.id === settings.controlId && group.isControl) ||
      !groups.some((group) => group.id === settings.variantId && !group.isControl)
    )
      return

    await sectionHTML(drawer)
    if (!drawer.isConnected || drawer.openCancelled) return
    let group = user.getTestGroup(settings.experimentId)
    if (!group) {
      // Public API: let Intelligems apply its configured traffic split.
      await user.assignTestGroup(settings.experimentId, '', { randomAssignIfNullish: true })
      group = user.getTestGroup(settings.experimentId)
    }
    if (group?.id === settings.variantId) return 'variant'
    return 'control'
  }

  async function open(drawer, show) {
    if (!settled) {
      if (!opening) {
        drawer.setAttribute('aria-busy', 'true')
        opening = activate(drawer)
          .catch(() => {
            // Never force a group or switch late after a fallback.
            return 'control'
          })
          .then(async (selectedArm = 'control') => {
            if (!drawer.isConnected || drawer.openCancelled) return
            const html = await pricingHTML(drawer, selectedArm)
            if (!drawer.isConnected || drawer.openCancelled) return
            mount(drawer, html, selectedArm)
            pricingReady = true
          })
          .catch(() => {
            pricingError(drawer)
          })
          .finally(() => {
            settled = pricingReady
            if (!settled) opening = null
            drawer.removeAttribute('aria-busy')
          })
      }
      await opening
    }
    // The editor replaces the whole section with its server-default Control.
    // Restore the chosen arm without another assignment on section reconnect.
    if (pricingReady && drawer.isConnected && !drawer.openCancelled && drawer.dataset.cartArm !== arm) {
      try {
        mount(drawer, await sectionHTML(drawer), arm)
      } catch {
        pricingError(drawer)
      }
    }
    if (drawer.isConnected) show()
  }

  window.SnuggliCartExperiment = {
    open,
    parseSection,
    get arm() {
      return arm
    },
  }
})()
